import React from "react";
import {
  ArrowUpRight,
  Clock3,
  FileSearch,
  ImageDown,
  Link as LinkIcon,
  Palette,
  Route,
  Server,
  Sparkles,
  Tags,
  Type,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  BLOG_WORKFLOW_STARTERS,
  PUBLISHED_BLOG_POSTS,
  getBlogPostPath,
  type BlogVisualKind,
} from "../blog/posts";
import SiteFooter from "../components/SiteFooter";
import SiteTopNav from "../components/SiteTopNav";
import { TOOL_CATALOG } from "../tools/catalog";
import "./blog.css";

const VISUAL_ICONS: Record<BlogVisualKind, LucideIcon> = {
  seo: FileSearch,
  workflow: Route,
  images: ImageDown,
  writing: Type,
  design: Palette,
  links: LinkIcon,
  dns: Server,
};

function BlogVisual({
  kind,
  compact = false,
}: {
  kind: BlogVisualKind;
  compact?: boolean;
}) {
  const Icon = VISUAL_ICONS[kind];

  return (
    <div
      className={`blog-visual blog-visual--${kind} ${
        compact ? "blog-visual--compact" : ""
      }`.trim()}
      aria-hidden="true"
    >
      <div className="blog-visual__grid" />
      <div className="blog-visual__icon">
        <Icon strokeWidth={1.8} />
      </div>
      <div className="blog-visual__lines">
        <span />
        <span />
        <span />
      </div>
      <div className="blog-visual__nodes">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="blog-visual__rail" />
    </div>
  );
}

export default function Blog() {
  const featuredPost = PUBLISHED_BLOG_POSTS[0];

  return (
    <div className="ng-blog">
      <SiteTopNav />

      <main>
        <section className="blog-hero">
          <div className="blog-hero__copy">
            <div className="blog-eyebrow">Editorial / Workflow Notes</div>
            <h1>Blog</h1>
            <p>
              Practical notes for people shipping faster with browser tools:
              SEO preflights, image cleanup, text utilities, design handoffs,
              and the small checks that keep work moving.
            </p>
          </div>

          <div className="blog-hero__stats" aria-label="Blog highlights">
            <div>
              <strong>{PUBLISHED_BLOG_POSTS.length}</strong>
              <span>Published article</span>
            </div>
            <div>
              <strong>{TOOL_CATALOG.length}</strong>
              <span>Tools covered</span>
            </div>
            <div>
              <strong>{BLOG_WORKFLOW_STARTERS.length}</strong>
              <span>Workflow starters</span>
            </div>
          </div>
        </section>

        <section className="blog-feature" aria-labelledby="featured-post-title">
          <article className="blog-feature__story" data-content-kind="published-article">
            <div className="blog-post-meta">
              <span>{featuredPost.category}</span>
              <span>{featuredPost.date}</span>
              <span>
                <Clock3 aria-hidden="true" strokeWidth={1.8} />
                {featuredPost.readTime}
              </span>
            </div>
            <h2 id="featured-post-title">{featuredPost.title}</h2>
            <p>{featuredPost.excerpt}</p>
            <Link className="blog-action blog-action--primary" to={getBlogPostPath(featuredPost)}>
              <span>Read article</span>
              <ArrowUpRight aria-hidden="true" strokeWidth={1.8} />
            </Link>
          </article>

          <img
            alt={featuredPost.article.heroImage.alt}
            className="blog-feature__image"
            src={featuredPost.article.heroImage.src}
          />
        </section>

        <section className="blog-board" aria-labelledby="workflow-starters-title">
          <div className="blog-section-head">
            <div>
              <div className="blog-eyebrow">Not articles / Direct actions</div>
              <h2 id="workflow-starters-title">Workflow Starters</h2>
            </div>
            <p>
              These are short problem prompts that open the relevant working
              tool. They are not presented as published posts, dated reporting,
              or substitutes for a complete article.
            </p>
          </div>

          <div className="blog-grid">
            {BLOG_WORKFLOW_STARTERS.map((starter) => (
              <article
                className={`blog-card blog-accent-${starter.accent}`}
                data-content-kind="workflow-starter"
                key={starter.slug}
              >
                <BlogVisual compact kind={starter.visual} />
                <div className="blog-card__body">
                  <div className="blog-post-meta">
                    <span>Workflow starter</span>
                    <span>{starter.category}</span>
                  </div>
                  <h3>{starter.title}</h3>
                  <p>{starter.excerpt}</p>
                  <Link className="blog-inline-link" to={starter.related.to}>
                    <span>{starter.related.label}</span>
                    <ArrowUpRight aria-hidden="true" strokeWidth={1.8} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="blog-playbook" aria-labelledby="playbook-title">
          <div>
            <div className="blog-eyebrow">Start Here</div>
            <h2 id="playbook-title">Build a faster launch check</h2>
            <p>
              A good workflow does not need a giant stack. Start with sitemap
              coverage, verify redirects and DNS, then tighten the page assets.
            </p>
          </div>

          <div className="blog-playbook__actions">
            <Link className="blog-action" to="/tools/sitemap-robots-explorer">
              <Tags aria-hidden="true" strokeWidth={1.8} />
              <span>Sitemap scan</span>
            </Link>
            <Link className="blog-action" to="/tools/redirect-analyzer">
              <Route aria-hidden="true" strokeWidth={1.8} />
              <span>Redirect check</span>
            </Link>
            <Link className="blog-action" to="/tools/image-compressor">
              <Sparkles aria-hidden="true" strokeWidth={1.8} />
              <span>Asset cleanup</span>
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter inverted />
    </div>
  );
}
